import { SettingFilled } from '@ant-design/icons';
import { localStorageAction } from '@vindr/utils/helpers/storage';
import { MenuProps, Radio, RadioChangeEvent, Switch } from 'antd';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../../../ui/Dropdown';
import { useCaptureState, useCaptureUpdater } from '../context';
import { DefaultInputDevice, KEY_DEFAULT_CAMERA } from '../context/state';
import { getListResolution } from '../helper/webcam-helper';
import './SettingIcon.css';

const SettingGroup = ({
  title,
  children,
  setActive,
  checked,
}: {
  title: string;
  children: React.ReactNode;
  setActive?: (checked: boolean) => void;
  checked?: boolean;
}) => {
  return (
    <div className="setting-group">
      <div className="setting-group-header flex justify-between">
        <p>{title}</p>
        {checked !== undefined && setActive && (
          <Switch size="small" onChange={setActive} checked={checked} />
        )}
      </div>
      <div className="setting-wrapper">{children}</div>
    </div>
  );
};

const SettingIcon = ({ className = '', disabled }: { className?: string; disabled: boolean }) => {
  const { t } = useTranslation('Vindoc');
  const {
    enableMic,
    selectedMic,
    micList,
    selectedVideo,
    selectedResolution,
    resolutionList,
    cameraList,
    isFullScreen,
  } = useCaptureState();
  const dispatch = useCaptureUpdater();

  useEffect(() => {
    if (isEmpty(selectedVideo)) return;

    getListResolution(selectedVideo).then((res) => {
      if (!res.length) return;
      dispatch({ type: 'SET_RESOLUTION_LIST', payload: res });
    });
  }, [dispatch, selectedVideo]);

  const changeSelect = useCallback(
    (e: RadioChangeEvent, type: 'CHANGE_SELECTED_CAMERA' | 'CHANGE_SELECTED_MICROPHONE') =>
      dispatch({ type, payload: e.target.value }),
    [dispatch]
  );

  const items: MenuProps['items'] = [
    micList.length && {
      key: '1',
      title: t('Microphone'),
      label: (
        <SettingGroup
          title={t('Microphone')}
          checked={enableMic}
          setActive={(val) => dispatch({ type: 'CHANGE_ENABLE_MIC', payload: val })}
        >
          <Radio.Group
            className="flex flex-col"
            onChange={(e) => changeSelect(e, 'CHANGE_SELECTED_MICROPHONE')}
            value={selectedMic}
            disabled={!enableMic}
          >
            {micList?.map((mic) => (
              <Radio value={mic} key={mic.deviceId}>
                {mic.label}
              </Radio>
            ))}
          </Radio.Group>
        </SettingGroup>
      ),
    },
    cameraList.length && {
      key: '2',
      title: t('Camera'),
      label: (
        <SettingGroup title={t('Camera')}>
          <Radio.Group
            className="flex flex-col"
            onChange={(e) => {
              localStorageAction.set(KEY_DEFAULT_CAMERA, e.target.value);
              changeSelect(e, 'CHANGE_SELECTED_CAMERA');
            }}
            value={selectedVideo}
          >
            {cameraList?.map((camera) => (
              <Radio
                value={camera}
                key={camera.deviceId}
                disabled={!!DefaultInputDevice.find((x) => new RegExp(x, 'ig').test(camera.label))}
              >
                {camera.label}
              </Radio>
            ))}
          </Radio.Group>
        </SettingGroup>
      ),
    },
    // cameraList.length && {
    //   key: '3',
    //   title: t('Resolution'),
    //   label: (
    //     <SettingGroup title={t('Resolution')}>
    //       <Radio.Group
    //         className="flex flex-col"
    //         onChange={(e) =>
    //           dispatch({
    //             type: 'CHANGE_RESOLUTION',
    //             payload: e.target.value,
    //           })
    //         }
    //         value={selectedResolution.width + 'x' + selectedResolution.height}
    //       >
    //         {resolutionList?.map((size) => (
    //           <Radio key={size.height} value={`${size.width}x${size.height}`}>
    //             {size.width}x{size.height}
    //           </Radio>
    //         ))}
    //       </Radio.Group>
    //     </SettingGroup>
    //   ),
    // },
  ].filter(Boolean);

  return (
    <Dropdown menu={{ items }} placement="topRight" trigger={['click']}>
      <SettingFilled
        className={classnames(`btn-setting p-0 pr-0 text-[26px] drop-shadow-lg ${className}`, {
          'disabled cursor-not-allowed opacity-50': disabled,
          'sm:text-[20px] lg:text-[28px]': isFullScreen,
        })}
      />
    </Dropdown>
  );
};

export default SettingIcon;
